import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { getGuide, getGuides, trackEvent } from '../lib/api';
import SEO from '../components/SEO';

function InlineCTA({ heading, sub, button }) {
  return (
    <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 my-4">
      <div>
        <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>{heading}</h3>
        <p className="text-sm text-slate-500">{sub}</p>
      </div>
      <Link to="/blueprint" className="flex-shrink-0">
        <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
          {button} <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
    </div>
  );
}

function ComparisonBlock({ left, right }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">{left.label}</p>
        <div className="space-y-2">
          {left.points.map((p, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0 mt-1.5"></div>
              <span className="text-[13px] text-slate-600">{p}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-emerald-50/50 rounded-2xl border border-emerald-100/50 p-6">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-emerald-600 mb-3">{right.label}</p>
        <div className="space-y-2">
          {right.points.map((p, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5"></div>
              <span className="text-[13px] text-slate-700">{p}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SEOPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [allGuides, setAllGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    Promise.all([
      getGuide(slug).then(r => setPage(r.data)).catch(() => setPage(null)),
      getGuides().then(r => setAllGuides(r.data.guides || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
    trackEvent({ event_type: 'page_view', page: `guide_${slug}` });
  }, [slug]);

  if (loading) return <div className="min-h-screen pt-16 flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (!page) return (
    <div className="min-h-screen pt-16 flex flex-col items-center justify-center gap-4">
      <p className="text-slate-500">Guide not found</p>
      <Link to="/guides"><Button variant="outline" className="rounded-full">Browse All Guides</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen pt-16" data-testid={`seo-page-${slug}`}>
      {page && (
        <SEO
          title={`${page.title} — Burlington Real Estate Guide`}
          description={page.meta || page.subtitle || ''}
          canonical={`/guides/${slug}`}
          type="article"
          article={{ dateModified: page.updated_at || page.created_at }}
          breadcrumbs={[
            { name: 'Home', url: '/' },
            { name: 'Guides', url: '/guides' },
            { name: page.category, url: '/guides' },
            { name: page.title, url: `/guides/${slug}` },
          ]}
          faq={page.sections?.filter(s => s.type === 'cta').length > 0 ? null : undefined}
        />
      )}
      <section className="mesh-gradient-section py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to="/guides" className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" /> All Guides
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600">{page.category}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl" style={{ fontFamily: "'Instrument Serif', serif" }}>
            {page.title}
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl leading-relaxed">{page.subtitle}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl py-12 md:py-16 space-y-6">
          {page.sections?.map((section, i) => {
            if (section.type === 'text') return (
              <div key={i} className="pb-6 border-b border-slate-100 last:border-0">
                <h2 className="text-2xl md:text-3xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>{section.heading}</h2>
                <p className="text-base text-slate-500 leading-relaxed">{section.body}</p>
              </div>
            );
            if (section.type === 'list') return (
              <div key={i} className="pb-6 border-b border-slate-100 last:border-0">
                <h2 className="text-2xl md:text-3xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>{section.heading}</h2>
                <div className="space-y-2.5">
                  {section.items?.map((item, j) => (
                    <div key={j} className="flex items-start gap-2.5 p-3 bg-white rounded-xl border border-slate-200/80">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-2"></div>
                      <span className="text-[13px] text-slate-700 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
            if (section.type === 'cta') return <InlineCTA key={i} heading={section.heading} sub={section.sub} button={section.button} />;
            if (section.type === 'comparison') return <ComparisonBlock key={i} left={section.left} right={section.right} />;
            return null;
          })}
        </div>

        {/* Deep Links */}
        <div className="pb-12 md:pb-16 border-t border-slate-100 pt-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {page.related_pages?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-4">Related guides</p>
                <div className="space-y-2">
                  {page.related_pages.map(rSlug => {
                    const related = allGuides.find(g => g.slug === rSlug);
                    if (!related) return null;
                    return (
                      <Link key={rSlug} to={`/guides/${rSlug}`} className="block p-4 bg-white rounded-xl border border-slate-200/80 card-interactive" data-testid={`related-${rSlug}`}>
                        <span className="text-xs text-emerald-600 font-medium">{related.category}</span>
                        <p className="text-sm font-medium text-slate-900 mt-0.5">{related.title}</p>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {page.related_neighbourhoods?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-4">Related neighbourhoods</p>
                <div className="space-y-2">
                  {page.related_neighbourhoods.map(nSlug => (
                    <Link key={nSlug} to={`/neighbourhoods/${nSlug}`} className="block p-4 bg-white rounded-xl border border-slate-200/80 card-interactive" data-testid={`neighbourhood-link-${nSlug}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                        <span className="text-sm font-medium text-slate-900 capitalize">{nSlug.replace(/-/g, ' ')}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mesh-gradient-hero noise-overlay relative rounded-3xl overflow-hidden p-10 md:p-14">
            <div className="relative z-10 max-w-lg">
              <h2 className="text-2xl md:text-3xl text-white tracking-tight leading-[1.15] mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Ready to see which areas <span className="italic">fit you?</span>
              </h2>
              <p className="text-sm text-white/40 mb-6">6 questions. Under 3 minutes. All 21 Burlington neighbourhoods scored against your priorities.</p>
              <Link to="/blueprint">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                  Start My Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
